// ── Marking a program line by line (`EX-CMP01`) ──────────────────────────────
// `ProgramSourceView` draws a program as one `<pre>` of coloured runs. Two
// programs side by side need more than colour: whole lines washed in the colour
// of the region they belong to, and inside them the names that were renamed.
// This cuts the same runs (or the plain source while they load) into lines, and
// each line into segments at every renamed range's edges, without dropping or
// adding a character: joining every line's segments with "\n" gives `source`
// back. Pure, so the cut is tested apart from the view.

import type { ProgramRun } from "./program-highlighter";

export type MarkedSegment = {
  text: string;
  /** The highlighter's class, or "" for plain text. */
  className: string;
  /** Inside a renamed name. */
  renamed: boolean;
};

/**
 * The program as lines of segments.
 *
 * `renamed` is a list of `[start, end)` character offsets into `source`; they
 * may come in any order and may touch, and a range that runs past the end of
 * the source is clipped to it.
 */
export function segmentProgramLines(
  source: string,
  runs: readonly ProgramRun[] | null,
  renamed: ReadonlyArray<readonly [number, number]>,
): MarkedSegment[][] {
  const inRenamed = new Uint8Array(source.length);
  for (const [from, to] of renamed) {
    const start = Math.max(0, Math.min(from, source.length));
    const end = Math.max(start, Math.min(to, source.length));
    inRenamed.fill(1, start, end);
  }

  const lines: MarkedSegment[][] = [[]];
  const base = runs ?? [{ text: source, className: "" }];
  let offset = 0;

  for (const run of base) {
    let pieceStart = 0;
    const flush = (end: number) => {
      if (end <= pieceStart) return;
      lines[lines.length - 1]!.push({
        text: run.text.slice(pieceStart, end),
        className: run.className,
        renamed: inRenamed[offset + pieceStart] === 1,
      });
    };
    for (let index = 0; index < run.text.length; index += 1) {
      if (run.text[index] === "\n") {
        flush(index);
        lines.push([]);
        pieceStart = index + 1;
        continue;
      }
      if (index > pieceStart && inRenamed[offset + index] !== inRenamed[offset + index - 1]) {
        flush(index);
        pieceStart = index;
      }
    }
    flush(run.text.length);
    offset += run.text.length;
  }
  return lines;
}
